/**
 * Error sanitization utilities for Sentry.
 * Prevents module references (like 'db') and other problematic properties from being copied into error objects.
 *
 * @module sentry/error-sanitizer
 */

/**
 * Keys that should be excluded when copying error properties.
 * These are properties that can cause serialization issues or contain module references.
 */
const EXCLUDED_ERROR_KEYS = new Set([
  'request',
  'response',
  'message',
  'name',
  'stack',
  'db', // Exclude db module reference (can cause "E.db.getUserByEmail is not a function" errors)
]);

/**
 * Sanitize error by removing problematic properties that can cause serialization issues.
 * Creates a safe copy of the error without module references, request/response objects, or class instances.
 *
 * **Critical**: This function MUST exclude 'db' and other module references to prevent
 * the "E.db.getUserByEmail is not a function" error that occurs when error objects
 * are serialized with module references attached.
 *
 * @param error - Error to sanitize (can be Error instance or any object)
 * @returns Sanitized Error instance with only safe properties
 *
 * @example
 * ```typescript
 * try {
 *   await someOperation();
 * } catch (error) {
 *   const safeError = sanitizeErrorForSentry(error);
 *   Sentry.captureException(safeError);
 * }
 * ```
 */
export function sanitizeErrorForSentry(error: unknown): Error {
  if (!error || typeof error !== 'object') {
    return new Error(String(error || 'Unknown error'));
  }

  // If error doesn't have request/response properties, return as-is (but still ensure it's an Error)
  if (!('request' in error) && !('response' in error)) {
    return error instanceof Error ? error : new Error(String(error));
  }

  // Create a safe copy without request/response properties
  const safeError = new Error(error instanceof Error ? error.message : 'Unknown error');
  safeError.name = error instanceof Error ? error.name : 'Error';
  if (error instanceof Error && error.stack) {
    safeError.stack = error.stack;
  }

  // Copy other enumerable properties (except excluded keys and module references)
  // CRITICAL: This loop must exclude 'db' to prevent serialization errors
  for (const key in error) {
    if (!EXCLUDED_ERROR_KEYS.has(key)) {
      try {
        const value = (error as Record<string, unknown>)[key];
        // Only copy primitive values and plain objects - skip functions and class instances
        // This prevents module references and other problematic objects from being copied
        // Note: null is allowed (it's a valid primitive value)
        if (value === null) {
          // Explicitly allow null values
          (safeError as unknown as Record<string, unknown>)[key] = null;
        } else if (
          value !== undefined &&
          typeof value !== 'function' &&
          !(value instanceof Error) &&
          !(value instanceof Request) &&
          !(value instanceof Response)
        ) {
          if (typeof value === 'object') {
            // Only copy plain objects, not class instances
            // This prevents DatabaseClient and other class instances from being copied
            if ((value as object).constructor === Object) {
              (safeError as unknown as Record<string, unknown>)[key] = value;
            }
          } else {
            (safeError as unknown as Record<string, unknown>)[key] = value;
          }
        }
      } catch {
        // Skip properties that can't be copied
      }
    }
  }

  return safeError;
}

/**
 * Get the set of excluded error keys.
 * Exported for testing purposes.
 *
 * @returns Set of excluded error keys (readonly at TypeScript level)
 * @internal
 */
export function getExcludedErrorKeys(): ReadonlySet<string> {
  // Return as readonly - TypeScript enforces immutability
  return EXCLUDED_ERROR_KEYS as ReadonlySet<string>;
}
